import { Checklist } from "./checklist";
import { tr } from "./localization";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as Dialog from "@radix-ui/react-dialog";
import {
  Send,
  Lock,
  Layers,
  UserRound,
  X,
  Check,
  Paperclip,
  ChevronRight,
  ChevronLeft,
  CalendarDays,
  Columns3,
  Link as LinkIcon,
  CheckCircle,
  Eye,
  Trash2,
  ChevronDown,
  Flag,
  MoreHorizontal,
  Copy,
  Upload,
  Archive,
  CornerDownLeft,
} from "lucide-react";
import { PopMenu, menuIsOpen, type PopOption } from "./pop-menu";
import { toast, Toaster } from "sonner";
import { api, ApiError } from "./api";
import type { Column, Task, User } from "./types";
import { dayInAlmaty, dueText, whenShort, kinds, priorities } from "./utils";
import {
  useApp,
  err,
  Avatar,
  userName,
  Button,
  Modal,
  Loading,
  Empty,
  ErrorState,
  perform,
  PriorityIcon,
} from "./shared";

export function TaskLink() {
  const { taskId } = useParams(),
    navigate = useNavigate();
  const q = useQuery({
    queryKey: ["task", taskId],
    queryFn: () => api<Task>("/tasks/" + taskId),
    retry: false,
  });
  useEffect(() => {
    if (q.data) navigate("/?task=" + taskId, { replace: true });
  }, [taskId, q.data]);
  if (q.error instanceof ApiError && q.error.status === 404)
    return (
      <div className="no-access-page">
        <span className="no-access-art">
          <Lock size={32} />
        </span>
        <h1>{tr("Задача не найдена или нет доступа")}</h1>
        <p>
          {tr("Возможно, задачу удалили или у вас нет прав на её просмотр.")}
        </p>
        <div className="surface">
          <h3>{tr("Что можно сделать")}</h3>
          <ul>
            <li>
              {tr("Попросить автора задачи или владельца доски выдать доступ")}
            </li>
            <li>{tr("Проверить, что вы вошли под своей учётной записью")}</li>
          </ul>
        </div>
        <div className="no-access-actions">
          <Link className="btn primary" to="/">
            {tr("К моим задачам")}
          </Link>
          <Link className="btn" to="/boards">
            {tr("К доскам")}
          </Link>
        </div>
      </div>
    );
  return q.error ? (
    <ErrorState error={q.error} retry={() => q.refetch()} />
  ) : (
    <Loading />
  );
}

export function TaskDrawer({ id, close }: { id: string; close: () => void }) {
  const { users, refresh } = useApp(),
    qc = useQueryClient();
  const { me } = useApp();
  const touchY = useRef<number | null>(null);
  const q = useQuery({
    queryKey: ["task", id],
    queryFn: () => api<Task>("/tasks/" + id),
    retry: false,
  });
  useEffect(() => {
    if (q.error instanceof ApiError && [403, 404].includes(q.error.status)) {
      close();
      refresh();
    }
  }, [q.error]);
  const columnQuery = useQuery({
    queryKey: ["columns", q.data?.board_id],
    queryFn: () => api<Column[]>("/boards/" + q.data!.board_id + "/columns"),
    enabled: !!q.data,
  });
  const [reason, setReason] = useState<{ action: string } | null>(null),
    [comment, setComment] = useState(""),
    [mention, setMention] = useState(""),
    [grant, setGrant] = useState(false),
    [saving, setSaving] = useState(false);
  const commentSending = useRef(false);
  const [sendingComment, setSendingComment] = useState(false);
  const currentTaskId = useRef(id);
  currentTaskId.current = id;
  const [reasonText, setReasonText] = useState("");
  const [showMention, setShowMention] = useState(false);
  const onBoard = useLocation().pathname.startsWith("/boards/");
  const [justSaved, setJustSaved] = useState(false);
  const savedTimer = useRef<number>(0);
  useEffect(() => () => window.clearTimeout(savedTimer.current), []);
  const task = q.data;
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setComment(localStorage.getItem("draft:" + id) || "");
    setMention("");
    setShowMention(false);
    setGrant(false);
  }, [id]);
  async function save(changes: Record<string, unknown>, message?: string) {
    if (!task) return;
    setSaving(true);
    try {
      const res = await api<{ task: Task; undo_token: string }>(
        "/tasks/" + id,
        "PATCH",
        { version: task.version, ...changes },
      );
      qc.setQueryData(["task", id], { ...task, ...res.task });
      refresh();
      q.refetch();
      setJustSaved(true);
      window.clearTimeout(savedTimer.current);
      savedTimer.current = window.setTimeout(() => setJustSaved(false), 2600);
      toast.success(message || tr("Изменения сохранены"), {
        action: {
          label: tr("Отменить"),
          onClick: async () => {
            try {
              await api("/undo/" + res.undo_token, "POST");
              refresh();
            } catch (e) {
              err(e);
            }
          },
        },
      });
      return true;
    } catch (e) {
      err(e, () => save(changes));
      if (e instanceof ApiError && e.status === 409) q.refetch();
    } finally {
      setSaving(false);
    }
  }
  async function uploadFiles(files: File[]) {
    for (const f of files) {
      try {
        const form = new FormData();
        form.append("file", f);
        await api(`/tasks/${id}/attachments`, "POST", form);
      } catch (e) {
        err(e);
      }
    }
    q.refetch();
    refresh();
  }
  async function removeResource(
    resourceId: string,
    type: "attachments" | "comments",
  ) {
    try {
      const res = await api<{ undo_token: string }>(
        `/tasks/${id}/${type}/${resourceId}`,
        "DELETE",
      );
      q.refetch();
      toast.success(tr("Удалено"), {
        action: {
          label: tr("Отменить"),
          onClick: async () => {
            try {
              await api("/undo/" + res.undo_token, "POST");
              q.refetch();
            } catch (e) {
              err(e);
            }
          },
        },
      });
    } catch (e) {
      err(e);
    }
  }
  const action = (a: string) => {
    if (["return", "reopen"].includes(a)) {
      setReason({ action: a });
      setReasonText("");
    } else if (task) perform(task, a, refresh);
  };
  const visibleIds = Array.from(
    document.querySelectorAll<HTMLElement>("[data-task-id]"),
  )
    .map((e) => e.dataset.taskId!)
    .filter((value, index, all) => all.indexOf(value) === index);
  const currentIndex = visibleIds.indexOf(id);
  const { openTask } = useApp();
  const step = (direction: number) => {
    const next = visibleIds[currentIndex + direction];
    if (next) openTask(next, true);
  };
  const renderActions = () =>
    task
      ? [
          ["start", tr("Принять в работу")],
          [
            "submit",
            task.needs_review ? tr("Сдать на проверку") : tr("Выполнено"),
          ],
          ["accept", tr("Принять")],
          ["return", tr("Вернуть")],
          ["withdraw", tr("Отозвать")],
          ["reopen", tr("Открыть заново")],
        ]
          .filter(
            ([a]) =>
              task.permissions[a] &&
              (
                {
                  start: ["todo", "backlog"],
                  submit: ["todo", "backlog", "in_progress"],
                  accept: ["review"],
                  return: ["review"],
                  withdraw: ["review"],
                  reopen: ["done"],
                }[a] || []
              ).includes(task.kind),
          )
          .sort((x, y) => (x[0] === "return" ? -1 : y[0] === "return" ? 1 : 0))
          .map(([a, title]) => (
            <Button
              key={a}
              className={
                ["submit", "accept", "start"].includes(a) ? "primary" : ""
              }
              disabled={!navigator.onLine}
              onClick={() => action(a)}
            >
              {a === "accept" && <Check size={14} aria-hidden="true" />}
              {a === "return" && (
                <CornerDownLeft size={14} aria-hidden="true" />
              )}
              {title}
            </Button>
          ))
      : null;
  return (
    <>
      <Dialog.Root
        open
        onOpenChange={(v) => {
          if (!v) close();
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="drawer-overlay" />
          <Dialog.Content
            className="task-drawer"
            tabIndex={-1}
            onOpenAutoFocus={(e) => {
              e.preventDefault();
              (e.currentTarget as HTMLElement | null)?.focus?.();
            }}
            onEscapeKeyDown={(e) => {
              if (menuIsOpen()) e.preventDefault();
            }}
            onKeyDown={(e) => {
              if (
                (e.target as HTMLElement).closest(
                  "input,textarea,select,[contenteditable=true]",
                ) ||
                e.ctrlKey ||
                e.metaKey ||
                e.altKey
              )
                return;
              if (["ArrowUp", "k", "K"].includes(e.key)) {
                e.preventDefault();
                step(-1);
              }
              if (["ArrowDown", "j", "J"].includes(e.key)) {
                e.preventDefault();
                step(1);
              }
            }}
            onTouchStart={(e) => {
              if (e.touches[0].clientY < 115)
                touchY.current = e.touches[0].clientY;
            }}
            onTouchEnd={(e) => {
              if (
                touchY.current !== null &&
                e.changedTouches[0].clientY - touchY.current > 90
              )
                close();
              touchY.current = null;
            }}
            onDragOver={(e) => {
              if (task?.permissions.attachment) e.preventDefault();
            }}
            onDrop={(e) => {
              if (task?.permissions.attachment) {
                e.preventDefault();
                uploadFiles(Array.from(e.dataTransfer.files));
              }
            }}
            onPaste={(e) => {
              if (
                task?.permissions.attachment &&
                e.clipboardData.files.length
              ) {
                e.preventDefault();
                uploadFiles(Array.from(e.clipboardData.files));
              }
            }}
          >
            <header>
              <span className="drawer-id">
                IMS-{id.slice(0, 6).toUpperCase()}
              </span>
              {!onBoard && (
                <div className="drawer-navigation">
                  <Button
                    className="icon"
                    aria-label={tr("Предыдущая задача")}
                    title={tr("Предыдущая (K)")}
                    disabled={currentIndex <= 0}
                    onClick={() => step(-1)}
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  <Button
                    className="icon"
                    aria-label={tr("Следующая задача")}
                    title={tr("Следующая (J)")}
                    disabled={
                      currentIndex < 0 || currentIndex >= visibleIds.length - 1
                    }
                    onClick={() => step(1)}
                  >
                    <ChevronRight size={16} />
                  </Button>
                  {currentIndex >= 0 && visibleIds.length > 1 && (
                    <span className="drawer-position">
                      {currentIndex + 1} {tr("из")} {visibleIds.length}
                    </span>
                  )}
                </div>
              )}
              {onBoard && (
                <Button
                  className="ghost small drawer-copy"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      location.origin + "/tasks/" + id,
                    );
                    toast.success(tr("Ссылка скопирована"));
                  }}
                >
                  <LinkIcon size={14} aria-hidden="true" />{" "}
                  {tr("Копировать ссылку")}
                </Button>
              )}
              <span className="spacer" />
              <span
                className={"saved" + (justSaved ? " on" : "")}
                role="status"
                aria-live="polite"
              >
                {justSaved && (
                  <>
                    <Check size={14} /> {tr("Сохранено")}
                  </>
                )}
              </span>
              {task && task.role !== "guest" && !onBoard && (
                <Link
                  className="btn ghost small drawer-board-link"
                  to={`/boards/${task.board_id}`}
                  onClick={close}
                  aria-label={tr("Открыть на доске")}
                  title={tr("Открыть на доске")}
                >
                  <Columns3 size={16} />
                  <span>{tr("Открыть на доске")}</span>
                </Link>
              )}
              {task && (
                <PopMenu
                  triggerClass="btn icon"
                  ariaLabel={tr("Ещё")}
                  align="right"
                  trigger={<MoreHorizontal size={18} />}
                  options={[
                    {
                      value: "link",
                      label: (
                        <>
                          <LinkIcon size={15} /> {tr("Копировать ссылку")}
                        </>
                      ),
                    },
                    {
                      value: "duplicate",
                      label: (
                        <>
                          <Copy size={15} /> {tr("Дублировать задачу")}
                        </>
                      ),
                      disabled: !task.permissions.comment,
                      why: tr("Дублировать могут участники доски"),
                    },
                    ...(task.permissions.restore
                      ? [{ value: "restore", label: tr("Восстановить задачу") }]
                      : []),
                    ...(task.permissions.archive && !task.is_archived
                      ? [
                          {
                            value: "archive",
                            separator: true,
                            danger: true,
                            label: (
                              <>
                                <Archive size={15} /> {tr("Архивировать")}
                              </>
                            ),
                          } as PopOption,
                        ]
                      : []),
                  ]}
                  onPick={async (v) => {
                    if (v === "link") {
                      navigator.clipboard.writeText(
                        location.origin + "/tasks/" + id,
                      );
                      toast.success(tr("Ссылка скопирована"));
                    } else if (v === "duplicate") {
                      try {
                        const res = await api<{ task: Task }>(
                          "/tasks",
                          "POST",
                          {
                            board_id: task.board_id,
                            title: task.title + tr(" (копия)"),
                            description: task.description,
                            assignee_id: task.assignee_id,
                            priority: task.priority,
                            due_date: task.due_date,
                            due_time: task.due_time,
                            review_required: task.review_required,
                          },
                        );
                        refresh();
                        toast.success(tr("Задача продублирована"), {
                          action: {
                            label: tr("Открыть"),
                            onClick: () => openTask(res.task.id),
                          },
                        });
                      } catch (e) {
                        err(e);
                      }
                    } else if (v === "restore") {
                      await perform(task, "restore", refresh);
                    } else if (v === "archive") {
                      await perform(task, "archive", refresh);
                      close();
                    }
                  }}
                />
              )}
              <Dialog.Close className="btn icon" aria-label={tr("Закрыть")}>
                <X size={20} />
              </Dialog.Close>
            </header>
            <Dialog.Title className="sr-only">
              {tr("Карточка задачи")}
            </Dialog.Title>
            <Dialog.Description className="sr-only">
              {tr("Свойства, результат и обсуждение задачи")}
            </Dialog.Description>
            {q.isPending ? (
              <Loading />
            ) : q.error ? (
              q.error instanceof ApiError && q.error.status === 404 ? (
                <Empty
                  text={tr(
                    "Задача не найдена или нет доступа. Попросите автора или владельца доски выдать доступ.",
                  )}
                />
              ) : (
                <ErrorState error={q.error} retry={() => q.refetch()} />
              )
            ) : (
              task && (
                <div className="drawer-body">
                  <label className="sr-only" htmlFor="task-title">
                    {tr("Название задачи")}
                  </label>
                  <textarea
                    rows={1}
                    key={task.id + task.title}
                    className="drawer-title"
                    id="task-title"
                    defaultValue={task.title}
                    maxLength={300}
                    disabled={!task.permissions.title || saving}
                    onBlur={(e) => {
                      if (e.target.value !== task.title)
                        save({ title: e.target.value });
                    }}
                  />
                  {task.kind === "review" && (
                    <div className="banner review">
                      <Eye size={20} />
                      <div className="banner-text">
                        <strong>
                          {task.permissions.accept
                            ? tr("Исполнитель сдал задачу")
                            : tr("Задача на приёмке у автора")}
                        </strong>
                        <div className="muted">
                          {task.permissions.accept
                            ? `${userName(users, task.assignee_id)} · ${tr("ждёт вашей приёмки")}`
                            : task.permissions.withdraw
                              ? tr(
                                  "Пока автор не принял — сдачу можно отозвать",
                                )
                              : tr("Автор проверит результат")}
                        </div>
                      </div>
                      <div className="banner-actions">{renderActions()}</div>
                    </div>
                  )}
                  {task.kind === "done" && (
                    <div className="banner ok">
                      <CheckCircle size={20} />
                      <div className="banner-text">
                        <strong>{tr("Задача принята")}</strong>
                        {task.permissions.reopen && (
                          <div className="muted">
                            {tr(
                              "Если нужно что-то доработать — откройте заново и укажите причину",
                            )}
                          </div>
                        )}
                      </div>
                      <div className="banner-actions">{renderActions()}</div>
                    </div>
                  )}
                  {!["review", "done"].includes(task.kind) && (
                    <div className="task-actions">{renderActions()}</div>
                  )}
                  <dl className="props">
                    <dt>
                      <UserRound size={15} aria-hidden="true" />{" "}
                      {tr("Исполнитель")}
                    </dt>
                    <dd>
                      <PopMenu
                        ariaLabel={tr("Исполнитель")}
                        disabled={!task.permissions.assignee || saving}
                        why={
                          ["backlog", "todo", "in_progress"].includes(task.kind)
                            ? tr("Исполнителя меняют автор и владелец доски")
                            : tr(
                                "Исполнителя можно менять только в рабочих статусах — сначала верните задачу на доработку",
                              )
                        }
                        trigger={
                          <>
                            {task.assignee_id ? (
                              <>
                                <Avatar
                                  user={users.find(
                                    (u) => u.id === task.assignee_id,
                                  )}
                                />{" "}
                                <span className="prop-text">
                                  {userName(users, task.assignee_id)}
                                </span>
                              </>
                            ) : (
                              <span className="prop-text prop-no-icon muted">
                                {tr("Не назначен")}
                              </span>
                            )}
                            <ChevronDown size={14} aria-hidden="true" />
                          </>
                        }
                        options={[
                          {
                            value: "",
                            selected: !task.assignee_id,
                            label: tr("Не назначен"),
                          },
                          ...users
                            .filter(
                              (u) =>
                                u.is_active !== false ||
                                u.id === task.assignee_id,
                            )
                            .map((u) => ({
                              value: u.id,
                              selected: u.id === task.assignee_id,
                              label: (
                                <>
                                  <Avatar user={u} /> {u.name}
                                </>
                              ),
                            })),
                        ]}
                        onPick={(v) =>
                          v !== (task.assignee_id || "") &&
                          save(
                            { assignee_id: v || null },
                            v
                              ? `${tr("Исполнитель")}: ${userName(users, v)}`
                              : tr("Исполнитель снят"),
                          )
                        }
                      />
                    </dd>
                    <dt>
                      <Send size={15} aria-hidden="true" /> {tr("Автор")}
                    </dt>
                    <dd>
                      <span className="prop-btn static">
                        <Avatar
                          user={users.find((u) => u.id === task.author_id)}
                        />{" "}
                        <span className="prop-text">
                          {userName(users, task.author_id)}
                        </span>
                      </span>
                    </dd>
                    <dt>
                      <CalendarDays size={15} aria-hidden="true" /> {tr("Срок")}
                    </dt>
                    <dd>
                      <PopMenu
                        ariaLabel={tr("Срок")}
                        disabled={!task.permissions.due || saving}
                        why={tr(
                          "Срок меняют автор, исполнитель и владелец доски",
                        )}
                        trigger={
                          <>
                            <CalendarDays
                              className="prop-value-icon"
                              size={16}
                              aria-hidden="true"
                            />
                            {task.due_date ? (
                              <span className="prop-text">{dueText(task)}</span>
                            ) : (
                              <span className="prop-text muted">
                                {tr("Без срока")}
                              </span>
                            )}
                            {!!task.due_at &&
                              new Date(task.due_at) < new Date() &&
                              !task.submitted_at &&
                              !task.completed_at && (
                                <span className="due overdue">
                                  {tr("просрочено")}
                                </span>
                              )}
                            <ChevronDown size={14} aria-hidden="true" />
                          </>
                        }
                        options={(() => {
                          const day = (n: number) =>
                            dayInAlmaty(new Date(Date.now() + n * 86400000));
                          return [
                            { value: day(0), label: tr("Сегодня") },
                            { value: day(1), label: tr("Завтра") },
                            { value: day(7), label: tr("Через неделю") },
                            {
                              value: "",
                              label: tr("Без срока"),
                              separator: true,
                            },
                          ].map((o) => ({
                            ...o,
                            selected: (task.due_date || "") === o.value,
                          }));
                        })()}
                        onPick={(v) =>
                          save(
                            {
                              due_date: v || null,
                              due_time: v ? task.due_time : null,
                            },
                            tr("Срок изменён"),
                          )
                        }
                        footer={(closeMenu) => (
                          <TaskDueEditor
                            date={task.due_date || ""}
                            time={task.due_time?.slice(0, 5) || ""}
                            saving={saving}
                            onSave={async (date, time) => {
                              if (
                                await save(
                                  { due_date: date, due_time: time || null },
                                  tr("Срок изменён"),
                                )
                              )
                                closeMenu();
                            }}
                          />
                        )}
                      />
                    </dd>
                    <dt>
                      <Flag size={15} aria-hidden="true" /> {tr("Приоритет")}
                    </dt>
                    <dd>
                      <PopMenu
                        ariaLabel={tr("Приоритет")}
                        disabled={!task.permissions.priority || saving}
                        why={tr("Приоритет меняют автор и владелец доски")}
                        trigger={
                          <>
                            <PriorityIcon priority={task.priority} label />
                            <ChevronDown size={14} aria-hidden="true" />
                          </>
                        }
                        options={Object.entries(priorities).map(([k]) => ({
                          value: k,
                          selected: task.priority === k,
                          label: <PriorityIcon priority={k} label />,
                        }))}
                        onPick={(v) =>
                          v !== task.priority &&
                          save({ priority: v }, tr("Приоритет изменён"))
                        }
                      />
                    </dd>
                    <dt>
                      <Columns3 size={15} aria-hidden="true" /> {tr("Колонка")}
                    </dt>
                    <dd>
                      <PopMenu
                        ariaLabel={tr("Колонка")}
                        disabled={!task.permissions.move}
                        why={tr(
                          "Двигать задачу могут автор, исполнитель и владелец доски",
                        )}
                        trigger={
                          <>
                            <span className={"chip " + task.kind}>
                              <span className="dot" />
                              {columnQuery.data?.find(
                                (c) => c.id === task.column_id,
                              )?.name || kinds[task.kind]}
                            </span>
                            <ChevronDown size={14} aria-hidden="true" />
                          </>
                        }
                        options={(columnQuery.data || []).map((c) => {
                          const work = ["backlog", "todo", "in_progress"];
                          const blocked =
                            c.kind !== task.kind &&
                            (!work.includes(c.kind) ||
                              !work.includes(task.kind));
                          return {
                            value: c.id,
                            selected: c.id === task.column_id,
                            disabled: blocked,
                            why: ["review", "done"].includes(c.kind)
                              ? tr(
                                  "Сюда — только действиями «Сдать на проверку» и «Принять»",
                                )
                              : tr(
                                  "Из «На проверке» и «Готово» — только действиями «Вернуть», «Отозвать», «Открыть заново»",
                                ),
                            label: c.name,
                          };
                        })}
                        onPick={(v) =>
                          v !== task.column_id &&
                          perform(task, "move", refresh, { column_id: v })
                        }
                      />
                    </dd>
                    {!onBoard && (
                      <>
                        <dt>
                          <Layers size={15} aria-hidden="true" /> {tr("Доска")}
                        </dt>
                        <dd>
                          <span className="prop-btn static">
                            <span className="prop-text prop-no-icon">
                              {task.board_name}
                            </span>
                          </span>
                        </dd>
                      </>
                    )}
                  </dl>
                  {["backlog", "todo", "in_progress"].includes(task.kind) &&
                    task.permissions.review_required &&
                    task.author_id !== task.assignee_id && (
                      <label className="checkbox-label review-toggle">
                        <input
                          type="checkbox"
                          checked={task.review_required}
                          onChange={(e) =>
                            save(
                              { review_required: e.target.checked },
                              e.target.checked
                                ? tr("Приёмка включена")
                                : tr("Приёмка выключена"),
                            )
                          }
                        />
                        {tr("Приёмка автором")}
                      </label>
                    )}
                  <label className="dr-desc-label">
                    {tr("Описание")}
                    <textarea
                      className="dr-desc"
                      key={id + "desc"}
                      defaultValue={task.description}
                      rows={3}
                      maxLength={20000}
                      disabled={!task.permissions.description}
                      onBlur={(e) => {
                        if (e.target.value !== task.description)
                          save({ description: e.target.value });
                      }}
                      placeholder={tr(
                        "Что нужно сделать и что считать результатом",
                      )}
                    />
                  </label>
                  <Checklist
                    key={task.id}
                    task={task}
                    refresh={() => {
                      void q.refetch();
                      refresh();
                    }}
                  />
                  <div className="dr-label">
                    {tr("Вложения")} · {task.attachments?.length || 0}
                  </div>
                  <div className="attachments">
                    {task.attachments?.map((a) => (
                      <div className="attachment-item" key={a.id}>
                        <a href={"/api/v1/attachments/" + a.id}>
                          {a.mime.startsWith("image/") ? (
                            <img
                              className="attachment-thumb"
                              alt={a.file_name}
                              src={
                                "/api/v1/attachments/" + a.id + "?preview=true"
                              }
                            />
                          ) : (
                            <Paperclip size={15} />
                          )}
                          {a.file_name}
                          <small>
                            {Math.round(a.size / 1024)} {tr("КБ")}
                          </small>
                        </a>
                        {task.permissions.attachment &&
                          (a.uploader_id === me.id ||
                            task.role === "owner") && (
                            <Button
                              className="icon danger"
                              aria-label={tr("Удалить ") + a.file_name}
                              onClick={() =>
                                removeResource(a.id, "attachments")
                              }
                            >
                              <Trash2 size={15} />
                            </Button>
                          )}
                      </div>
                    ))}
                  </div>
                  {task.permissions.attachment && (
                    <>
                      <Button
                        className="attachment-dropzone"
                        onClick={() => inputRef.current?.click()}
                      >
                        <Upload size={16} aria-hidden="true" />
                        <span>
                          {tr("Перетащите файлы сюда, вставьте из буфера или")}{" "}
                          <u>{tr("выберите")}</u>
                        </span>
                      </Button>
                      <input
                        ref={inputRef}
                        type="file"
                        multiple
                        className="sr-only"
                        aria-label={tr("Выбрать вложение")}
                        onChange={(e) => {
                          uploadFiles(Array.from(e.target.files || []));
                          e.target.value = "";
                        }}
                      />
                    </>
                  )}
                  {!!task.access?.some((a) => a.reason === "mentioned") && (
                    <section>
                      <h3>{tr("Доступ через упоминание")}</h3>
                      {task.access
                        ?.filter((a) => a.reason === "mentioned")
                        .map((a) => (
                          <div className="member-row" key={a.id}>
                            {userName(users, a.user_id)}
                            {(task.role === "owner" ||
                              task.author_id === me.id ||
                              a.user_id === me.id) && (
                              <Button
                                onClick={async () => {
                                  try {
                                    await api(
                                      `/tasks/${id}/access/${a.user_id}`,
                                      "DELETE",
                                    );
                                    refresh();
                                    if (
                                      a.user_id === me.id &&
                                      task.role === "guest"
                                    )
                                      close();
                                  } catch (e) {
                                    err(e);
                                  }
                                }}
                              >
                                {a.user_id === me.id
                                  ? tr("Покинуть задачу")
                                  : tr("Убрать из задачи")}
                              </Button>
                            )}
                          </div>
                        ))}
                    </section>
                  )}
                  <div className="dr-label">
                    {tr("Комментарии")} · {task.comments?.length || 0}
                  </div>
                  <TaskTimeline
                    task={task}
                    users={users}
                    me={me}
                    remove={(id) => removeResource(id, "comments")}
                  />
                </div>
              )
            )}
            {task && (
              <div className="drawer-composer">
                {" "}
                {task.permissions.comment ? (
                  <form
                    className="composer"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      if (commentSending.current || !comment.trim()) return;
                      commentSending.current = true;
                      setSendingComment(true);
                      const postedTaskId = id;
                      try {
                        await api(`/tasks/${id}/comments`, "POST", {
                          text: comment,
                          mention_ids: mention ? [mention] : [],
                          grant_ids: grant && mention ? [mention] : [],
                        });
                        localStorage.removeItem("draft:" + postedTaskId);
                        if (currentTaskId.current === postedTaskId) {
                          setComment("");
                          setMention("");
                          setGrant(false);
                        }
                        q.refetch();
                        refresh();
                      } catch (e) {
                        err(e);
                      } finally {
                        commentSending.current = false;
                        setSendingComment(false);
                      }
                    }}
                  >
                    <label className="sr-only" htmlFor="comment">
                      {tr("Комментарий")}
                    </label>
                    <Avatar user={me} />
                    <textarea
                      id="comment"
                      disabled={sendingComment}
                      value={comment}
                      rows={1}
                      maxLength={5000}
                      onChange={(e) => {
                        setComment(e.target.value);
                        if (/@[^\s]*$/.test(e.target.value))
                          setShowMention(true);
                        localStorage.setItem("draft:" + id, e.target.value);
                      }}
                      placeholder={tr("Комментарий…")}
                      required
                      onKeyDown={(e) => {
                        if (
                          e.key === "Enter" &&
                          !e.nativeEvent.isComposing &&
                          (!e.shiftKey || e.ctrlKey || e.metaKey)
                        ) {
                          e.preventDefault();
                          if (comment.trim() && navigator.onLine)
                            e.currentTarget.form?.requestSubmit();
                        }
                      }}
                    />
                    {task.permissions.attachment && (
                      <Button
                        className="icon small composer-file"
                        aria-label={tr("Прикрепить файл")}
                        onClick={() => inputRef.current?.click()}
                      >
                        <Paperclip size={16} />
                      </Button>
                    )}
                    <Button
                      className="icon small composer-mention"
                      disabled={sendingComment}
                      aria-label={tr("Упомянуть коллегу")}
                      aria-expanded={showMention}
                      onClick={() => setShowMention((v) => !v)}
                    >
                      {" "}
                      @{" "}
                    </Button>
                    {(showMention || mention) && (
                      <label className="mention-control">
                        {tr("Упомянуть")}
                        <select
                          disabled={sendingComment}
                          value={mention}
                          onChange={(e) => {
                            setMention(e.target.value);
                            setGrant(false);
                          }}
                        >
                          <option value="">{tr("Без упоминания")}</option>
                          {users.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                    {mention && task.permissions.grant && (
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          disabled={sendingComment}
                          checked={grant}
                          onChange={(e) => setGrant(e.target.checked)}
                        />
                        {tr(
                          "Дать этому сотруднику доступ, если он ещё не видит задачу",
                        )}
                      </label>
                    )}
                    <Button
                      type="submit"
                      className="primary icon composer-send"
                      aria-label={tr("Отправить")}
                      title={tr("Отправить · Ctrl/⌘ Enter")}
                      disabled={
                        sendingComment || !comment.trim() || !navigator.onLine
                      }
                    >
                      <Send size={16} />
                    </Button>
                  </form>
                ) : (
                  <p className="muted">{tr("Доступ только для чтения")}</p>
                )}
              </div>
            )}
            <Toaster
              richColors
              position="bottom-right"
              duration={7000}
              offset={{ bottom: 84, right: 16 }}
              mobileOffset={{ bottom: 84, right: 12, left: 12 }}
            />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      <Modal
        open={!!reason}
        title={
          reason?.action === "return"
            ? tr("Вернуть на доработку")
            : tr("Открыть заново")
        }
        onClose={() => setReason(null)}
      >
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (task && reason) {
              const result = await perform(task, reason.action, refresh, {
                comment: reasonText,
              });
              if (result) setReason(null);
            }
          }}
        >
          <label>
            {tr("Причина")}
            <textarea
              autoFocus
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              required
              rows={4}
            />
          </label>
          <Button
            className="primary"
            type="submit"
            disabled={!reasonText.trim()}
          >
            {tr("Подтвердить")}
          </Button>
        </form>
      </Modal>
    </>
  );
}

export function TaskTimeline({
  task,
  users,
  me,
  remove,
}: {
  task: Task;
  users: User[];
  me: User;
  remove: (id: string) => void;
}) {
  const events = [
    ...(task.activity || [])
      .filter((a) => a.action !== "comment")
      .map((a) => ({ type: "activity" as const, data: a })),
    ...(task.comments || []).map((c) => ({
      type: "comment" as const,
      data: c,
    })),
  ].sort((a, b) => a.data.created_at.localeCompare(b.data.created_at));
  const labels: Record<string, string> = {
    created: tr("создал задачу"),
    updated: tr("изменил свойства"),
    move: tr("переместил задачу"),
    start: tr("принял в работу"),
    submit: tr("сдал задачу на приёмку"),
    accept: tr("принял результат"),
    return: tr("вернул на доработку"),
    reopen: tr("открыл заново"),
    undo: tr("отменил действие"),
    accept_undone: tr("отменил приёмку"),
    withdraw: tr("отозвал результат"),
    archive: tr("архивировал задачу"),
    restore: tr("восстановил задачу из архива"),
    checklist_add: tr("добавил пункт чек-листа"),
    checklist_rename: tr("переименовал пункт чек-листа"),
    checklist_toggle: tr("изменил выполнение пункта чек-листа"),
    checklist_delete: tr("удалил пункт чек-листа"),
    checklist_reorder: tr("изменил порядок пунктов чек-листа"),
    attachment_added: tr("добавил вложение"),
    attachment_deleted: tr("удалил вложение"),
    comment_deleted: tr("удалил комментарий"),
    access_revoked: tr("отозвал доступ"),
    reassigned: tr("сменил исполнителя"),
  };
  const tones: Record<string, string> = {
    submit: "review",
    accept: "ok",
    accept_undone: "review",
    return: "warn",
    reopen: "warn",
  };
  const whenText = (iso: string) => whenShort(iso);
  const renderEntry = (entry: (typeof events)[number]) =>
    entry.type === "activity" ? (
      <div className="system-event" key={entry.data.id}>
        <span
          className={"system-icon " + (tones[entry.data.action] || "")}
          aria-hidden="true"
        >
          {entry.data.action === "submit" ? (
            <Eye size={15} />
          ) : entry.data.action === "return" ||
            entry.data.action === "reopen" ? (
            <CornerDownLeft size={15} />
          ) : (
            <CheckCircle size={15} />
          )}
        </span>
        <span>
          {userName(users, entry.data.user_id)}{" "}
          {labels[entry.data.action] || entry.data.action}
          {Boolean(entry.data.payload.owner_action) &&
            " " + tr("(от имени владельца)")}
        </span>
        <time dateTime={entry.data.created_at}>
          {whenText(entry.data.created_at)}
        </time>
      </div>
    ) : (
      <article className="comment" key={entry.data.id}>
        <Avatar user={users.find((u) => u.id === entry.data.author_id)} />
        <div className="bubble">
          <div className="bubble-head">
            <strong>
              {userName(users, entry.data.author_id).split(" ")[0]}
            </strong>
            <small>{whenText(entry.data.created_at)}</small>
            {task.permissions.comment &&
              (entry.data.author_id === me.id || task.role === "owner") && (
                <button
                  type="button"
                  className="comment-delete"
                  aria-label={tr("Удалить комментарий")}
                  title={tr("Удалить комментарий")}
                  onClick={() => remove(entry.data.id)}
                >
                  <Trash2 size={14} aria-hidden="true" />
                </button>
              )}
          </div>
          <p>
            {entry.data.text.split(/(@[\p{L}-]+)/u).map((part, i) =>
              part.startsWith("@") ? (
                <span className="mention" key={i}>
                  {part}
                </span>
              ) : (
                part
              ),
            )}
          </p>
        </div>
      </article>
    );
  const comments = events.filter((entry) => entry.type === "comment");
  const history = events.filter((entry) => entry.type === "activity");
  return (
    <>
      <div className="timeline">
        {comments.length ? (
          comments.map(renderEntry)
        ) : (
          <p className="muted">{tr("Комментариев пока нет")}</p>
        )}
      </div>
      <details className="task-history" key={task.id}>
        <summary>
          <span>
            {tr("История задачи")} · {history.length}
          </span>
          <ChevronDown size={16} aria-hidden="true" />
        </summary>
        <div className="timeline">
          {history.length ? (
            history.map(renderEntry)
          ) : (
            <p className="muted">{tr("История пока пуста")}</p>
          )}
        </div>
      </details>
    </>
  );
}

function TaskDueEditor({
  date,
  time,
  saving,
  onSave,
}: {
  date: string;
  time: string;
  saving: boolean;
  onSave: (date: string, time: string) => Promise<void>;
}) {
  const [draftDate, setDraftDate] = useState(date);
  const [draftTime, setDraftTime] = useState(time);
  return (
    <form
      className="pop-date"
      onSubmit={(event) => {
        event.preventDefault();
        if (!saving && draftDate) void onSave(draftDate, draftTime);
      }}
    >
      <label>
        {tr("Другая дата")}
        <input
          type="date"
          required
          value={draftDate}
          disabled={saving}
          onChange={(event) => setDraftDate(event.target.value)}
        />
      </label>
      <label>
        {tr("Время (необязательно)")}
        <input
          type="time"
          value={draftTime}
          disabled={saving}
          onChange={(event) => setDraftTime(event.target.value)}
        />
      </label>
      <button
        type="submit"
        className="btn primary"
        disabled={saving || !draftDate}
      >
        {tr("Применить")}
      </button>
    </form>
  );
}
